import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimeoutOptions {
  onTimeout: () => void;
  timeoutMs: number;
  enabled?: boolean;
}

/**
 * Custom hook to track user inactivity and trigger a callback after a specified timeout.
 * Tracks mouse movements, keyboard events, clicks, and scrolling.
 * 
 * @param onTimeout - Callback function to execute when timeout is reached
 * @param timeoutMs - Timeout duration in milliseconds
 * @param enabled - Whether the inactivity tracking is enabled (default: true)
 */
export const useInactivityTimeout = ({
  onTimeout,
  timeoutMs,
  enabled = true
}: UseInactivityTimeoutOptions) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Update last activity timestamp
    lastActivityRef.current = Date.now();

    // Set new timeout
    if (enabled) {
      timeoutIdRef.current = setTimeout(() => {
        console.log('⏰ Inactivity timeout reached - logging out user');
        onTimeout();
      }, timeoutMs);
    }
  }, [onTimeout, timeoutMs, enabled]);

  // Activity event handler
  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      // Clean up if disabled
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      return;
    }

    // Events to track for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity tracking to avoid excessive timer resets
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledHandleActivity = () => {
      if (!throttleTimeout) {
        handleActivity();
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledHandleActivity, { passive: true });
    });

    // Start the initial timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledHandleActivity);
      });
      
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [enabled, resetTimer, handleActivity]);

  // Return function to manually reset the timer
  return { resetTimer };
};
